import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "V2.0/common/table";
import { Heading } from "V2.0/common/heading";
import { Button } from "V2.0/common/button";
import * as campaignService from "V2.0/services/campaignService";
import { Badge, BadgeButton } from "V2.0/common/badge";
import Pagination from "V2.0/components/Pagination";
import { message, Tooltip } from "antd";
import {
  ClockIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  PlusIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowUpRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
} from "V2.0/common/dropdown";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { BulkContact } from "./BulkUpload";
import { TrashIcon } from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogBody,
} from "V2.0/common/dialog";
import { DialogTitleWithClose } from "V2.0/HomePages/HomeModule/CustomAPIs/components/Dialog/DialogTitleWithClose";
import { useOutletContext, useNavigate, Link } from "react-router-dom";
import { Select } from "V2.0/common/select";
import { StartAndEndDateField } from "V2.0/HomePages/Downloads/components/StartAndEndDateField";
import InputComponent from "V2.0/components/InputComponent";
import { useAllAssistantOptions } from "./useAllAssistantOptions";
import { useAssistants } from "../Assistants";
import { ConversationSourceIcon } from "V2.0/HomePages/Threads/components/conversation_item";
import dayjs from "dayjs";
import { useDebouncedSearch } from "V2.0/HomePages/Threads/models/useDebouncedSearch";
import { showErrorToast } from "V2.0/utils";
import BulkDelete from "./BulkDelete";
import CampaignReportDownload from "./common/CampaignReportDownload";
import { CAMPAIGN_TYPE, SingleCampaignAdd } from "./SingleCampaignAdd";
import CampaignsLoading from "./common/CampaignLoading";
import { CampaignTypeIcons } from "../Campaigns/common/CampaignTypeIcons";
import CampaignsEmpty from "./common/CampaignEmpty";
import CampaignStatusBadge, {
  STATUS,
} from "../Campaigns/common/CampaignStatusBadge";
import { Listbox, ListboxOption } from "V2.0/common/listbox";
import CampaignsNotFound from "./common/CampaignsNotFound";
import CampaignObjectsTable from "./common/CampaignObjectsTable";
import CampaignReportModal from "./common/CampaignReportModal";

const formatFollowUpTime = (timestamp) => {
  const date = new Date(Number(timestamp));
  return dayjs(date).format("MMM DD, YYYY, HH:mm");
};

export const CAMPAIGN_STATUS = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  ONGOING: "Ongoing",
  FAILED: "Failed",
  UNANSWERED: "Unanswered",
  VOICEMAIL: "Voicemail",
  BLOCKED: "Blocked",
  DELIVERED: "Delivered",
  REJECTED: "Rejected",
  QUEUED: "Queued",
  CallPutOnHold: "CallPutOnHold",
};

export const CAMPAIGN_STATUS_LABEL_COLORS = {
  [CAMPAIGN_STATUS.COMPLETED]: "green",
  [CAMPAIGN_STATUS.DELIVERED]: "green",
  [CAMPAIGN_STATUS.VOICEMAIL]: "violet",
  [CAMPAIGN_STATUS.ONGOING]: "yellow",
  [CAMPAIGN_STATUS.SCHEDULED]: "amber",
  [CAMPAIGN_STATUS.UNANSWERED]: "pink",
  [CAMPAIGN_STATUS.FAILED]: "red",
  [CAMPAIGN_STATUS.BLOCKED]: "red",
  [CAMPAIGN_STATUS.REJECTED]: "red",
  [CAMPAIGN_STATUS.QUEUED]: "amber",
  [CAMPAIGN_STATUS.CallPutOnHold]: "blue",
};

const campaignStatusOptions = Object.values(CAMPAIGN_STATUS).map((status) => {
  return { id: status, label: status, value: status };
});

const campaignStatusOptionsWithDefault = [
  { id: "none", label: "(None)", value: "" },
  ...campaignStatusOptions,
];

function stripNonDigits(input) {
  return input.replace(/\D/g, ""); // \D matches any non-digit character
}

const transformCampaigns = (campaigns, assistants) => {
  console.log({ assistants });
  if (assistants.length > 0) {
    const assistantsMap = assistants.reduce((acc, assistant) => {
      return {
        ...acc,
        [assistant._id]: assistant,
      };
    }, {});

    const updatedCampaigns = campaigns.map((campaign) => {
      const { assistant } = campaign;
      return {
        ...campaign,
        assistant: assistantsMap[assistant],
      };
    });

    return updatedCampaigns;
  }

  return campaigns;
};

function useCampaigns(projectId) {
  const [campaigns, setCampaigns] = useState({
    totalPages: 0,
    totalCampaigns: 0,
    campaigns: [],
    currentPage: 1,
    pageSize: 50,
    isLoading: true,
    isReset: false,
    searchTerm: "",
  });

  const [filterParams, setFilterParams] = useState({
    callStatus: "",
    startDate: "",
    endDate: "",
    assistantId: "",
  });

  const isLoading = campaigns.isLoading;
  const setIsLoading = (state) =>
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isLoading: state,
      };
    });

  const updateDate = (date) => {
    const { startDate, endDate } = date;

    setFilterParams((prevState) => {
      return {
        ...prevState,
        startDate,
        endDate,
      };
    });
  };

  const updateCallStatus = (callStatus) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        callStatus,
      };
    });
  };

  const updateAssistantId = (assistantId) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        assistantId,
      };
    });
  };

  const fetchCampaigns = useCallback(
    async function fetchCampaigns() {
      try {
        setIsLoading(true);

        const response = await campaignService.getAllCalls(projectId);
        const { data } = response;

        setCampaigns(data.reverse());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }
    },
    [projectId],
  );

  const fetchPaginatedCampaigns = useCallback(
    async (projectId, page, pageSize) => {
      setIsLoading(true);
      try {
        const response = await campaignService.getAllCampaignsPaginated(
          projectId,
          { page, pageSize },
        );
        if (response.status === 200) {
          const { data } = response;
          // const campaingsWithAssistants = transformCampaigns(
          //   data.campaigns,
          //   assistants
          // );
          setCampaigns({
            ...data,
            campaigns: data.campaigns,
            isLoading: false,
          });
        }
      } catch (e) {
        showErrorToast(e, "Failed to get campaigns");
        setIsLoading(false);
      }
    },
    [],
  );

  const fetchFilteredCampaigns = useCallback(
    async (projectId, page, pageSize, searchTerm) => {
      setIsLoading(true);
      const { startDate, endDate, assistantId, callStatus } = filterParams;

      const params = {
        page,
        pageSize,
      };

      const payload = {
        ...(Boolean(searchTerm) ? { searchTerm } : {}),
        ...(Boolean(startDate) ? { startDate } : {}),
        ...(Boolean(endDate) ? { endDate } : {}),
        ...(Boolean(assistantId) ? { assistantId } : {}),
        ...(Boolean(callStatus) ? { callStatus } : {}),
      };

      try {
        const response = await campaignService.getFilteredCampaignsPaginated(
          projectId,
          params,
          payload,
        );
        const { data } = response;
        // const campaingsWithAssistants = transformCampaigns(data.campaigns);
        setCampaigns({
          ...data,
          campaigns: data.campaigns,
          isLoading: false,
        });
      } catch (e) {
        showErrorToast(e, "Failed to get campaigns");
        setIsLoading(false);
      }
    },
    [filterParams],
  );

  const refetchCampaings = (searchTerm) => {
    const { assistantId, startDate, endDate, callStatus } = filterParams;
    const isFiltersApplied =
      Boolean(assistantId) ||
      Boolean(startDate) ||
      Boolean(endDate) ||
      Boolean(callStatus);

    if (isFiltersApplied) {
      fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
    } else {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  };

  const resetFilters = () => {
    setFilterParams({
      startDate: "",
      endDate: "",
      callStatus: "",
      assistantId: "",
    });
    fetchPaginatedCampaigns(projectId, 1, 50);
  };

  const resetAssistant = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        assistantId: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const resetCallStatus = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        callStatus: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const resetDate = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        startDate: "",
        endDate: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const addUserToCampaign = useCallback(
    async (data, onSuccess, onError) => {
      try {
        const response = await campaignService.addToCampaign(projectId, data);
        onSuccess(response);
        refetchCampaings("");
      } catch (error) {
        return onError(error);
      }
    },
    [projectId],
  );

  const deleteUserFromCampaign = useCallback(
    async (campaignId, onSuccess, onError) => {
      try {
        const response = await campaignService.deleteFromCampaign({
          projectId,
          campaignId,
        });

        onSuccess(response);
        refetchCampaings("");
      } catch (error) {
        onError(error);
      }
    },
    [projectId],
  );

  useEffect(() => {
    if (campaigns.isReset) {
      refetchCampaings(campaigns.searchTerm);
    }
  }, [campaigns.isReset]);

  return {
    campaignData: campaigns,
    isCampaignsLoading: isLoading,
    fetchCampaigns,
    fetchPaginatedCampaigns,
    fetchFilteredCampaigns,
    addUserToCampaign,
    deleteUserFromCampaign,
    assistantId: filterParams.assistantId,
    callStatus: filterParams.callStatus,
    startDate: filterParams.startDate,
    endDate: filterParams.endDate,
    updateAssistantId,
    updateCallStatus,
    updateDate,
    resetFilters,
    resetAssistant,
    resetCallStatus,
    resetDate,
  };
}

function DeleteConfirmation({ isOpen, onClose, onConfirm, item }) {
  return (
    isOpen && (
      <Dialog
        open={isOpen}
        onClose={onClose}
      >
        <DialogTitle className="font-medium">
          <DialogTitleWithClose
            title={`Delete ${item.userNumber} from campaign?`}
            onClose={onClose}
          />
        </DialogTitle>
        <DialogActions>
          <Button
            outline
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            <TrashIcon /> Delete
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
}

function Filters(props) {
  const {
    onClose,
    onFiltersReset,
    onFiltersApply,
    updateCallStatus,
    callStatus,
    startDate,
    endDate,
    updateDate,
    assistantOptions,
    filterAssistantId,
    updateAssistantId,
    disableApply,
  } = props;

  return (
    <Dialog
      size="sm"
      open={true}
      onClose={onClose}
    >
      <DialogTitle>Filters</DialogTitle>
      <DialogBody>
        <div className="flex flex-col items-start gap-y-8 w-full">
          <div className="flex w-full flex-col items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Status:</p>
            <Listbox
              as="div"
              className="my-2"
              onChange={(value) => updateCallStatus(value)}
              value={callStatus}
            >
              {campaignStatusOptionsWithDefault.map((option) => (
                <ListboxOption
                  key={option.id}
                  value={option.value}
                >
                  {option.label}
                </ListboxOption>
              ))}
            </Listbox>
          </div>
          <div className="flex flex-col w-full items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Date Range:</p>
            <StartAndEndDateField
              hideLabel={true}
              error=""
              date={[startDate, endDate]}
              setError={() => {}}
              setDate={updateDate}
            />
          </div>
          <div className="flex flex-col w-full  items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Assistant:</p>
            <div className="w-full">
              <InputComponent
                type="select"
                options={assistantOptions}
                value={filterAssistantId}
                onChange={(e) => updateAssistantId(e.target.value)}
              />
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        {!disableApply ? (
          <Button
            outline
            onClick={onFiltersReset}
          >
            Reset Filters
          </Button>
        ) : (
          <Button
            outline
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        <Button
          disabled={disableApply}
          onClick={onFiltersApply}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FilterApplied(props) {
  const { children, onClose } = props;
  return (
    <BadgeButton
      className="border rounded-lg"
      color="white"
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-x-4">
        {children} <XMarkIcon className="size-4" />
      </div>
    </BadgeButton>
  );
}

function SingleCampaigns(props) {
  const { projectId, assistants, assistantsRecord, isViewOnly } = props;
  const navigate = useNavigate();

  const [addContactDialog, setAddContactDialog] = useState({
    isOpen: false,
    campaignType: undefined,
  });

  let [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const [showDelete, setShowDelete] = useState(null);
  const [openFilters, setOpenFilters] = useState(false);

  // The campaign whose report is open, or null. The whole row object is held rather than an id:
  // every section except the conversation renders from fields the row already carries, so
  // keeping the id would mean looking the row back up on each render for no gain.
  const [reportCampaign, setReportCampaign] = useState(null);

  const searchElementRef = useRef(null);

  const { assistantOptions, assistantId, setAssistantId } =
    useAllAssistantOptions(assistants);
  const initialLoadRef = useRef(true);
  const { searchTerm, updateSearchTerm } = useDebouncedSearch();

  const {
    campaignData,
    isCampaignsLoading: pageLoading,
    fetchPaginatedCampaigns,
    addUserToCampaign,
    deleteUserFromCampaign,
    assistantId: filterAssistantId,
    callStatus,
    startDate,
    endDate,
    resetFilters,
    updateAssistantId,
    updateDate,
    updateCallStatus,
    fetchFilteredCampaigns,
    resetAssistant,
    resetDate,
    resetCallStatus,
  } = useCampaigns(projectId);

  const itemsPerPage = campaignData.pageSize; // Define how many items per page
  const currentPage = campaignData.currentPage;
  const filteredCampaigns = campaignData.campaigns;

  // const { handlePageChange, paginatedItems: filteredAndPaginatedCampaigns } =
  //   usePagination(filteredCampaigns, itemsPerPage);

  useEffect(() => {
    if (projectId) {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  }, [projectId]);

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    if (!initialLoadRef.current && Boolean(searchTerm)) {
      fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
    } else {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  }, [searchTerm]);

  const handleApplyFilters = () => {
    fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
  };

  const isFiltersApplied =
    Boolean(filterAssistantId) ||
    Boolean(startDate) ||
    Boolean(endDate) ||
    Boolean(callStatus);

  const assistant = useMemo(() => {
    if (filterAssistantId) {
      const _assistant = assistants.find(
        (item) => item._id === filterAssistantId,
      );
      return _assistant;
    }

    return {};
  }, [filterAssistantId, assistants]);

  return (
    <>
      <div className="flex flex-col h-full mx-auto p-4 px-12 w-full">
        <div className="flex w-full flex-wrap items-end justify-between gap-4 mb-8 dark:border-white/10">
          <Heading>Single Campaigns</Heading>

          <div className="flex gap-4">
            {/* The report is requested with a POST, which the backend's authz treats as a write and
                denies to view-only roles, so the button is only offered to roles that can use it. */}
            {!isViewOnly && (
              <CampaignReportDownload
                projectId={projectId}
                assistants={assistants}
              />
            )}
            {!isViewOnly && (
              <div className="flex gap-x-2">
                <Dropdown>
                  <DropdownButton>
                    <PlusIcon /> Start Campaign
                    <ChevronDownIcon />
                  </DropdownButton>
                  <DropdownMenu className="min-w-48">
                    <DropdownSection className="flex gap-y-2 flex-col">
                      <DropdownItem
                        onClick={() =>
                          setAddContactDialog({
                            isOpen: true,
                            campaignType: CAMPAIGN_TYPE.SMS,
                          })
                        }
                      >
                        <div className="flex items-center gap-x-2">
                          <div>{CampaignTypeIcons[CAMPAIGN_TYPE.SMS]}</div>
                          <p className="text-sm">Sms</p>
                        </div>
                      </DropdownItem>
                      <DropdownItem
                        onClick={() =>
                          setAddContactDialog({
                            isOpen: true,
                            campaignType: CAMPAIGN_TYPE.CALL,
                          })
                        }
                      >
                        <div className="flex items-center gap-x-2">
                          <div>{CampaignTypeIcons[CAMPAIGN_TYPE.CALL]}</div>
                          <p className="text-sm">Call</p>
                        </div>
                      </DropdownItem>
                      <DropdownItem
                        onClick={() =>
                          setAddContactDialog({
                            isOpen: true,
                            campaignType: CAMPAIGN_TYPE.WHATSAPP,
                          })
                        }
                      >
                        <div className="flex items-center gap-x-2">
                          <div>{CampaignTypeIcons[CAMPAIGN_TYPE.WHATSAPP]}</div>
                          <p className="text-sm">Whatsapp</p>
                        </div>
                      </DropdownItem>
                      {/* <DropdownItem
                      onClick={() =>
                        setAddContactDialog({
                          isOpen: true,
                          campaignType: CAMPAIGN_TYPE.EMAIL,
                        })
                      }
                    >
                      <div className="flex items-center gap-x-2">
                        <div>{CampaignTypeIcons[CAMPAIGN_TYPE.EMAIL]}</div>
                        <p className="text-sm">Email</p>
                      </div>
                    </DropdownItem> */}
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col h-full flex-1 min-h-0 gap-y-8 w-full">
          <div className="flex justify-between gap-x-2">
            {/* {searchTerm && (
              <FilterApplied
                onClose={() => {
                  resetSearchTerm();
                }}
              >
                {searchTerm}
              </FilterApplied>
            )} */}
            <div className="flex gap-x-4">
              <div>
                <Button onClick={() => setOpenFilters(true)}>
                  <FunnelIcon /> Filters
                </Button>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="flex gap-x-2 flex-wrap gap-y-2">
                  {callStatus && (
                    <FilterApplied
                      onClose={() => {
                        resetCallStatus(searchTerm);
                      }}
                    >
                      <Badge color={CAMPAIGN_STATUS_LABEL_COLORS[callStatus]}>
                        {callStatus}
                      </Badge>
                    </FilterApplied>
                  )}
                  {startDate && endDate && (
                    <FilterApplied
                      onClose={() => {
                        resetDate(searchTerm);
                      }}
                    >
                      {formatFollowUpTime(startDate)} -{" "}
                      {formatFollowUpTime(endDate)}
                    </FilterApplied>
                  )}
                  {filterAssistantId && (
                    <FilterApplied
                      onClose={() => {
                        resetAssistant(searchTerm);
                      }}
                    >
                      {assistant?.name}
                    </FilterApplied>
                  )}
                  {isFiltersApplied && (
                    <Button
                      outline
                      onClick={resetFilters}
                    >
                      Clear All
                    </Button>
                  )}
                  {isFiltersApplied &&
                    !openFilters &&
                    !campaignData.isLoading &&
                    Boolean(campaignData.totalCampaigns) && !isViewOnly && (
                      <div className="flex">
                        <BulkDelete
                          totalItems={campaignData.totalCampaigns}
                          appliedFilters={{
                            callStatus,
                            startDate,
                            endDate,
                            filterAssistantId,
                          }}
                          refetchFilteredCampaigns={handleApplyFilters}
                          workspaceId={projectId}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="w-80">
              <input
                ref={searchElementRef}
                type="search"
                onChange={(e) => {
                  const sanitizedSearchTerm = stripNonDigits(e.target.value);
                  updateSearchTerm(sanitizedSearchTerm);
                }}
                placeholder="Search by Phone Number"
                className="w-full rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 placeholder-gray-400 focus:outline-none focus:border-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {filteredCampaigns &&
            filteredCampaigns.length === 0 &&
            !pageLoading &&
            !isFiltersApplied && <CampaignsEmpty />}

          {filteredCampaigns &&
            filteredCampaigns.length === 0 &&
            !pageLoading &&
            isFiltersApplied && <CampaignsNotFound />}

          {pageLoading && <CampaignsLoading />}

          {filteredCampaigns &&
            filteredCampaigns.length > 0 &&
            !pageLoading && (
              <div className="flex flex-col gap-y-2 flex-1 h-full w-full min-h-0">
                <CampaignObjectsTable
                  campaignObjects={filteredCampaigns}
                  assistantsRecord={assistantsRecord}
                  onDelete={setShowDelete}
                  onCampaignReport={setReportCampaign}
                  isViewOnly={isViewOnly}
                />
                <div className="relative bottom-0">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={campaignData.totalCampaigns}
                    itemsPerPage={campaignData.pageSize}
                    onPageChange={(page) => {
                      if (isFiltersApplied) {
                        fetchFilteredCampaigns(
                          projectId,
                          page,
                          campaignData.pageSize,
                        );
                      } else {
                        fetchPaginatedCampaigns(
                          projectId,
                          page,
                          campaignData.pageSize,
                        );
                      }
                    }}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
      {reportCampaign && (
        <CampaignReportModal
          isOpen={!!reportCampaign}
          onClose={() => setReportCampaign(null)}
          workspaceId={projectId}
          campaign={reportCampaign}
          assistantsRecord={assistantsRecord}
        />
      )}
      {addContactDialog.isOpen && (
        <SingleCampaignAdd
          addUserToCampaign={addUserToCampaign}
          dialogState={addContactDialog}
          onClose={() =>
            setAddContactDialog({
              isOpen: false,
              campaignType: undefined,
            })
          }
          projectId={projectId}
          campaignType={addContactDialog.campaignType}
        />
      )}
      {openFilters && (
        <Filters
          // searchTerm={searchTerm}
          // searchElementRef={searchElementRef}
          // updateSearchTerm={updateSearchTerm}
          startDate={startDate}
          endDate={endDate}
          updateDate={updateDate}
          assistantOptions={assistantOptions}
          filterAssistantId={filterAssistantId}
          updateAssistantId={updateAssistantId}
          callStatus={callStatus}
          updateCallStatus={updateCallStatus}
          disableApply={!isFiltersApplied}
          onClose={() => {
            setOpenFilters(false);
          }}
          onFiltersReset={() => {
            resetFilters();
            setOpenFilters(false);
          }}
          onFiltersApply={() => {
            handleApplyFilters();
            setOpenFilters(false);
          }}
        />
      )}
      <DeleteConfirmation
        isOpen={!!showDelete}
        onClose={() => setShowDelete(null)}
        onConfirm={() => {
          deleteUserFromCampaign(
            showDelete.campaignId,
            (response) => {
              if (response.status === 200 || response.status === 201) {
                message.success("Campaign deleted");
              }
            },
            (error) => {
              showErrorToast(error, "Failed to delete campaign");
              console.error(error);
            },
          );
          setShowDelete(null);
        }}
        item={showDelete}
      />
    </>
  );
}

export default SingleCampaigns;
